/**
 * Chat widget — entry point.
 *
 * Per docs/_archive/02-architecture.md sek. 9 i docs/_archive/07-mvp-minimal.md Chunk M.10.
 *
 * Module core; the served bundle is the loader (src/loader.ts) which imports
 * this. Install na stronie klienta to jeden tag z kluczem w ?w=:
 *
 *   <script type="module" src="https://cdn.kobot.chat/widget/v1/widget.es.js?w=wk_..."></script>
 *
 * Custom element rejestruje się przy import; ten moduł ustawia globalne
 * `window.<Brand>Widget = { WIDGET_VERSION, ChatWidget, open, close, setCustomer, showNudge }`
 * (np. window.KobotWidget).
 */

import { ChatWidget } from './components/chat-widget.js';
import { SearchBox } from './components/search-box.js';
import {
  open,
  close,
  setCustomer,
  showNudge,
  readGlobalNudge,
  GLOBAL_NAME,
  type KobotWidgetApi,
  type KobotOpenOptions,
  type KobotCustomerData,
  type KobotNudgeOptions,
} from './public-api.js';

declare global {
  interface Window {
    KobotWidget: KobotWidgetApi & { WIDGET_VERSION: string; ChatWidget: typeof ChatWidget };
  }
}

export const WIDGET_VERSION = '0.1.0';
export { ChatWidget, SearchBox, open, close, setCustomer, showNudge };
export type { KobotWidgetApi, KobotOpenOptions, KobotCustomerData, KobotNudgeOptions };
export { WidgetClient, WidgetApiException } from './api/widget-client.js';
export { resolveLocale, type WidgetLocale, type WidgetI18n } from './i18n/messages.js';

// ESM has no UMD auto-global, so assign window.<Brand>Widget explicitly here.
// Merge (don't overwrite) to stay safe if something already set it — host pages
// pre-create the object to pass `customer` prefill before the bundle loads.
// Global name (GLOBAL_NAME) is slug-derived in public-api.ts (e.g. "KobotWidget").
if (typeof window !== 'undefined') {
  const w = window as unknown as Record<string, unknown>;
  w[GLOBAL_NAME] = Object.assign((w[GLOBAL_NAME] as Record<string, unknown>) ?? {}, {
    WIDGET_VERSION,
    ChatWidget,
    open,
    close,
    setCustomer,
    showNudge,
  });
  installNudgeAccessor(w[GLOBAL_NAME] as Record<string, unknown>);
}

/**
 * `window.<Brand>Widget.nudge = {…}` must work whenever the host assigns it —
 * inline before the bundle (captured here), inline after the tag, or injected
 * later by a tag manager. A value present at load is forwarded to showNudge()
 * (buffered until the element registers); the property becomes an accessor so
 * every later assignment routes through showNudge() too (last call wins), while
 * reads still return the raw value the host set.
 */
function installNudgeAccessor(global: Record<string, unknown>): void {
  let current: unknown = readGlobalNudge();
  Object.defineProperty(global, 'nudge', {
    configurable: true,
    enumerable: true,
    get: () => current,
    set: (value: unknown) => {
      current = value;
      if (value != null) showNudge(value as KobotNudgeOptions);
    },
  });
  if (current != null) showNudge(current as KobotNudgeOptions);
}
